import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, PlayCircle, ArrowLeft, X } from 'lucide-react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/sea/index.css';
import '@videojs/http-streaming';
import 'videojs-hls-quality-selector';
import './App.css';
import { ornekVideolarData } from './data/ornekVideolarData';

const VIDEO_ASPECT_RATIO = 16 / 9;

const createDefaultLayoutMetrics = () => ({
  sidebarWidth: null,
  videoWidth: null,
  videoHeight: null,
  topBarHeight: null,
  variant: null,
  verticalDelta: 0
});

const OrnekVideolar = () => {
  const courseData = ornekVideolarData;

  // VideoUrl olan tüm dersleri bul
  const getVideoLessons = () => {
    const videoLessons = [];
    courseData.sections.forEach(section => {
      section.lessons.forEach(lesson => {
        if (lesson.videoUrl) {
          videoLessons.push(lesson);
        }
      });
    });
    return videoLessons;
  };

  // İlk lesson'u seç - videoUrl olan ilk dersi seç
  const getInitialLesson = () => {
    const videoLessons = getVideoLessons();
    return videoLessons.length > 0 ? videoLessons[0] : courseData.sections[0].lessons[0];
  };

  const getInitialSectionId = () => {
    return 0; // "Örnek Videolar" bölümü için özel ID
  };

  const [currentLesson, setCurrentLesson] = useState(getInitialLesson());
  const [expandedSections, setExpandedSections] = useState({ [getInitialSectionId()]: true });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layoutMetrics, setLayoutMetrics] = useState(createDefaultLayoutMetrics);
  const [isMobileLayout, setIsMobileLayout] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState('auto');
  const [selectedPlaybackRate, setSelectedPlaybackRate] = useState(1);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // Video.js refs
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const isEndedRef = useRef(false);
  const hasUserInteractedRef = useRef(false);
  const currentLessonRef = useRef(currentLesson);
  const selectedPlaybackRateRef = useRef(1);

  // Mobil cihaz kontrolü
  const isMobileDevice = useCallback(() => {
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isSmallScreen = window.innerWidth < 1024;
    const mobileIndicators = [hasTouchScreen, isMobileUA, isSmallScreen].filter(Boolean).length;
    return mobileIndicators >= 2;
  }, []);

  const evaluateDesktopLayout = useCallback(() => {
    if (typeof window === 'undefined') return;

    const pageWidth = window.innerWidth;
    const pageHeight = window.innerHeight;

    const mobile = isMobileDevice();
    setIsMobileLayout(mobile);

    if (mobile) {
      setLayoutMetrics(createDefaultLayoutMetrics());
      return;
    }

    const minSidebarWidth = pageWidth * 0.2;
    const maxSidebarWidth = pageWidth * 0.3;

    let topBarHeight = 56;
    if (pageWidth >= 1536) {
      topBarHeight = 88;
    } else if (pageWidth >= 640) {
      topBarHeight = 72;
    }

    const availableHeightForVideo = Math.max(pageHeight - topBarHeight, 0);
    const idealVideoWidth = (availableHeightForVideo * VIDEO_ASPECT_RATIO);
    const candidateSidebarWidth = pageWidth - idealVideoWidth;

    if (candidateSidebarWidth >= minSidebarWidth && candidateSidebarWidth <= maxSidebarWidth) {
      setLayoutMetrics({
        sidebarWidth: candidateSidebarWidth,
        videoWidth: idealVideoWidth,
        videoHeight: availableHeightForVideo,
        topBarHeight,
        variant: 'balanced',
        verticalDelta: 0
      });
      return;
    }

    if (candidateSidebarWidth < minSidebarWidth) {
      const videoWidth = pageWidth - minSidebarWidth;
      const videoHeight = videoWidth / VIDEO_ASPECT_RATIO;
      const leftoverSpace = pageHeight - (topBarHeight + videoHeight);

      setLayoutMetrics({
        sidebarWidth: minSidebarWidth,
        videoWidth,
        videoHeight,
        topBarHeight,
        variant: 'sidebarMin',
        verticalDelta: leftoverSpace
      });
      return;
    }

    const videoWidth = pageWidth - maxSidebarWidth;
    const videoHeight = videoWidth / VIDEO_ASPECT_RATIO;
    const overflow = (topBarHeight + videoHeight) - pageHeight;

    setLayoutMetrics({
      sidebarWidth: maxSidebarWidth,
      videoWidth,
      videoHeight,
      topBarHeight,
      variant: 'sidebarMax',
      verticalDelta: -overflow
    });
  }, [isMobileDevice]);

  // Safari tespiti
  const isSafari = () => {
    const ua = navigator.userAgent;
    const uaLower = ua.toLowerCase();
    
    if (ua.includes('CriOS') || ua.includes('FxiOS') || 
        (uaLower.includes('chrome') && !uaLower.includes('edg')) ||
        (uaLower.includes('firefox') && uaLower.includes('mobile'))) {
      return false;
    }
    
    return /^((?!chrome|android).)*safari/i.test(ua) || 
           (/iPad|iPhone|iPod/.test(ua) && !uaLower.includes('chrome') && !uaLower.includes('crios') && !uaLower.includes('fxios'));
  };

  // Video URL tipini belirle
  const getVideoType = (url) => {
    if (url.includes('.m3u8')) {
      return isSafari() ? 'application/vnd.apple.mpegurl' : 'application/x-mpegURL';
    } else if (url.includes('.mp4')) {
      return 'video/mp4';
    } else if (url.includes('.webm')) {
      return 'video/webm';
    } else if (url.includes('.ogg')) {
      return 'video/ogg';
    }
    return 'video/mp4';
  };

  // Kalite değiştirme fonksiyonu
  const changeQuality = useCallback(async (qualityValue) => {
    if (!playerRef.current) return;

    const currentSrc = playerRef.current.currentSrc();
    const currentVideoType = getVideoType(currentSrc || currentLesson.videoUrl);
    const isHLS = currentVideoType === 'application/x-mpegURL' || currentVideoType === 'application/vnd.apple.mpegurl';
    
    if (isHLS && playerRef.current.qualityLevels) {
      const qualityLevels = playerRef.current.qualityLevels();
      if (qualityLevels && qualityLevels.length > 0) {
        const levelsArray = Array.from(qualityLevels);
        
        if (qualityValue === 'auto') {
          levelsArray.forEach(level => {
            level.enabled = true;
          });
        } else {
          const heightMap = {
            '2160p': 2160,
            '1440p': 1440,
            '1080p': 1080,
            '720p': 720,
            '360p': 360
          };
          
          const targetHeight = heightMap[qualityValue];
          if (targetHeight) {
            const targetLevel = levelsArray.find(level => level.height === targetHeight);
            if (targetLevel) {
              levelsArray.forEach(level => {
                level.enabled = (level === targetLevel);
              });
            }
          }
        }
        return;
      }
    }
    
    let targetUrl;
    
    if (qualityValue === 'auto') {
      targetUrl = currentLesson.videoUrl;
    } else {
      const urlKey = `url${qualityValue}`;
      targetUrl = currentLesson[urlKey];
      
      if (!targetUrl) {
        console.warn(`Bu kalite için URL bulunamadı: ${qualityValue}`);
        return;
      }
    }
    
    try {
      const currentTime = playerRef.current.currentTime();
      const wasPlaying = !playerRef.current.paused();
      
      const videoType = getVideoType(targetUrl);
      playerRef.current.src({
        src: targetUrl,
        type: videoType
      });
      playerRef.current.load();
      
      playerRef.current.one('loadedmetadata', () => {
        playerRef.current.currentTime(currentTime);
        if (wasPlaying) {
          playerRef.current.play().catch(err => {
            console.error('Play hatası:', err);
          });
        }
      });
    } catch (error) {
      console.error('Kalite değiştirme hatası:', error);
    }
  }, [currentLesson]);

  // Video player'ı başlatan fonksiyon
  const initVideoPlayer = () => {
    if (!videoRef.current || playerRef.current) return;

    if (!document.body.contains(videoRef.current)) {
      return false;
    }

    // VideoUrl yoksa player başlatma
    if (!currentLesson.videoUrl) {
      return false;
    }

    try {
      const videoType = getVideoType(currentLesson.videoUrl);
      const isSafariBrowser = isSafari();
      
      playerRef.current = videojs(videoRef.current, {
        controls: true,
        responsive: true,
        fluid: true,
        poster: '/thumbnail_ornek.jpeg',
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
        html5: {
          vhs: {
            overrideNative: !isSafariBrowser,
            withCredentials: false
          },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false,
          hls: {
            withCredentials: false
          }
        },
        sources: [{
          src: currentLesson.videoUrl,
          type: videoType
        }]
      });

      playerRef.current.on('play', () => {
        if (!hasUserInteractedRef.current) {
          hasUserInteractedRef.current = true;
        }

        if (isEndedRef.current) { 
          isEndedRef.current = false; 
          restartVideo();
        }
      });

      playerRef.current.on('ended', () => {
        if (!isEndedRef.current) {
          isEndedRef.current = true;
          goToNextLesson();
        }
      });

      playerRef.current.on('fullscreenchange', () => {
        const isFullscreen = playerRef.current.isFullscreen();
        setIsFullscreen(isFullscreen);
      });

      playerRef.current.on('ratechange', () => {
        const currentRate = playerRef.current.playbackRate();
        if (currentRate && currentRate !== selectedPlaybackRateRef.current) {
          setSelectedPlaybackRate(currentRate);
          selectedPlaybackRateRef.current = currentRate;
        }
      });

      // Manuel quality selector ekleme
      playerRef.current.ready(() => {
        const addManualQualitySelector = () => {
          const controlBar = playerRef.current.el().querySelector('.vjs-control-bar');
          if (controlBar && !controlBar.querySelector('.vjs-safari-quality-selector')) {
            const button = document.createElement('button');
            button.className = 'vjs-control vjs-button vjs-safari-quality-selector';
            button.setAttribute('title', 'Kalite Seç');
            button.style.cssText = 'font-family: Arial, Helvetica, sans-serif; font-size: 13px; cursor: pointer; color: white; padding: 0 0.5em;';
            
            const buttonText = document.createElement('span');
            buttonText.textContent = 'Auto';
            buttonText.style.cssText = 'color: white; display: inline-block;';
            button.appendChild(buttonText);
            
            const screenReaderText = document.createElement('span');
            screenReaderText.className = 'vjs-control-text';
            screenReaderText.textContent = 'Kalite Seç';
            screenReaderText.style.cssText = 'display: none;';
            button.appendChild(screenReaderText);
            
            const menu = document.createElement('div');
            menu.className = 'vjs-menu vjs-safari-quality-menu';
            menu.style.cssText = 'display: none; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.9); width: 100px; z-index: 1000; border-radius: 4px; overflow: hidden;';
            
            const qualities = [
              { label: '2160p', value: '2160p' },
              { label: '1440p', value: '1440p' },
              { label: '1080p', value: '1080p' },
              { label: '720p', value: '720p' },
              { label: '360p', value: '360p' },
              { label: 'Auto', value: 'auto' }
            ];
            
            const updateSelectedItem = (currentQuality) => {
              const items = menu.querySelectorAll('.vjs-safari-quality-item');
              items.forEach(item => {
                const itemValue = qualities.find(q => q.label === item.textContent)?.value;
                if (itemValue === currentQuality) {
                  item.style.backgroundColor = 'rgba(147, 51, 234, 0.3)';
                  item.style.color = '#fff';
                  item.classList.add('vjs-selected');
                } else {
                  item.style.backgroundColor = 'transparent';
                  item.style.color = 'white';
                  item.classList.remove('vjs-selected');
                }
              });
              const selectedQualityObj = qualities.find(q => q.value === currentQuality);
              if (selectedQualityObj) {
                buttonText.textContent = selectedQualityObj.label;
              }
            };
            
            updateSelectedItem(selectedQuality);
            
            qualities.forEach(quality => {
              const item = document.createElement('div');
              item.className = 'vjs-menu-item vjs-safari-quality-item';
              item.style.cssText = 'padding: 10px 15px; cursor: pointer; color: white; font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.1); transition: background-color 0.2s;';
              item.textContent = quality.label;
              
              if (quality.value === selectedQuality) {
                item.style.backgroundColor = 'rgba(147, 51, 234, 0.3)';
                item.classList.add('vjs-selected');
              }
              
              item.addEventListener('mouseenter', () => {
                if (!item.classList.contains('vjs-selected')) {
                  item.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }
              });
              
              item.addEventListener('mouseleave', () => {
                if (!item.classList.contains('vjs-selected')) {
                  item.style.backgroundColor = 'transparent';
                } else {
                  item.style.backgroundColor = 'rgba(147, 51, 234, 0.3)';
                }
              });
              
              item.addEventListener('click', (e) => {
                e.stopPropagation();
                setSelectedQuality(quality.value);
                menu.style.display = 'none';
              });
              
              menu.appendChild(item);
            });
            
            button.updateSelectedItem = updateSelectedItem;
            button.appendChild(menu);
            button.style.position = 'relative';
            
            button.addEventListener('click', (e) => {
              e.stopPropagation();
              menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
            });
            
            const fullscreenButton = controlBar.querySelector('.vjs-fullscreen-control');
            if (fullscreenButton) {
              controlBar.insertBefore(button, fullscreenButton);
            } else {
              controlBar.appendChild(button);
            }
            
            setTimeout(() => {
              document.addEventListener('click', (e) => {
                if (!button.contains(e.target)) {
                  menu.style.display = 'none';
                }
              });
            }, 100);
          }
        };
        
        addManualQualitySelector();
        
        setTimeout(() => {
          const styleId = 'vjs-hls-quality-selector-styles';
          if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
              .vjs-control-bar {
                height: 40px !important;
                line-height: 40px !important;
              }
              .vjs-control {
                line-height: 40px !important;
              }
              .vjs-safari-quality-menu .vjs-safari-quality-item {
                line-height: 1em !important;
              }
            `;
            document.head.appendChild(style);
          }
        }, 500);
      });

      return true;
    } catch (error) {
      console.error('Error initializing Video.js:', error);
      return false;
    }
  };

  // Video.js player'ı başlat
  useEffect(() => {
    if (!videoRef.current) return;

    let attempts = 0;
    const maxAttempts = 10;
    
    const tryInit = () => {
      attempts++;
      
      if (initVideoPlayer()) {
        setPlayerReady(true);
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(tryInit, 100);
      } else {
        console.error('Failed to initialize Video.js after', maxAttempts, 'attempts');
      }
    };

    const timer = setTimeout(tryInit, 100);

    return () => {
      clearTimeout(timer);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      setPlayerReady(false);
    };
  }, []);

  // Browser fullscreen değişikliklerini dinle
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement === containerRef.current;
      setIsFullscreen(isFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    evaluateDesktopLayout();
    window.addEventListener('resize', evaluateDesktopLayout);
    return () => {
      window.removeEventListener('resize', evaluateDesktopLayout);
    };
  }, [evaluateDesktopLayout]);

  const computedSidebarStyle = !isMobileLayout && layoutMetrics.sidebarWidth
    ? { width: `${layoutMetrics.sidebarWidth}px` }
    : undefined;

  const getVideoContainerStyle = () => {
    if (isFullscreen) {
      return {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        background: 'black'
      };
    }

    if (isMobileLayout || !layoutMetrics.videoWidth || !layoutMetrics.videoHeight) {
      return {
        padding: '56.25% 0 0 0',
        position: 'relative',
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        background: 'black'
      };
    }

    return {
      position: 'relative',
      width: `${layoutMetrics.videoWidth}px`,
      height: `${layoutMetrics.videoHeight}px`,
      maxWidth: '100%',
      background: 'black'
    };
  };

  // Video URL değiştiğinde güncelle
  useEffect(() => {
    if (playerRef.current && currentLesson.videoUrl) {
      isEndedRef.current = false;

      playerRef.current.off('ended');

      const videoType = getVideoType(currentLesson.videoUrl);
      const isSafariBrowser = isSafari();
      const isHLS = videoType === 'application/x-mpegURL' || videoType === 'application/vnd.apple.mpegurl';
      
      if (isSafariBrowser && isHLS && playerRef.current.tech_) {
        playerRef.current.tech_.vhs = null;
      }
      
      playerRef.current.src({
        src: currentLesson.videoUrl,
        type: videoType
      });
      
      playerRef.current.load();
      playerRef.current.currentTime(0);

      playerRef.current.one('loadedmetadata', () => {
        const savedRate = selectedPlaybackRateRef.current;
        if (savedRate && savedRate !== 1) {
          playerRef.current.playbackRate(savedRate);
        }
      });

      playerRef.current.on('ended', () => {
        if (!isEndedRef.current) {
          isEndedRef.current = true;
          goToNextLesson();
        }
      });

      if (hasUserInteractedRef.current) {
        playerRef.current.play().catch(error => {
          if (error.name !== 'NotAllowedError') {
            console.error('Play hatası:', error);
          }
        });
      }
    }
  }, [currentLesson.videoUrl]);

  // Playback rate değiştiğinde uygula
  useEffect(() => {
    if (playerRef.current && playerReady && selectedPlaybackRate) {
      const currentRate = playerRef.current.playbackRate();
      if (currentRate !== selectedPlaybackRate) {
        playerRef.current.playbackRate(selectedPlaybackRate);
      }
    }
  }, [selectedPlaybackRate, playerReady]);

  // Seçilen kalite değiştiğinde videoyu güncelle
  useEffect(() => {
    if (playerReady && selectedQuality) {
      changeQuality(selectedQuality);
    }
    
    if (playerRef.current) {
      const qualityButton = playerRef.current.el().querySelector('.vjs-safari-quality-selector');
      if (qualityButton && qualityButton.updateSelectedItem) {
        qualityButton.updateSelectedItem(selectedQuality);
      }
    }
  }, [selectedQuality, playerReady, changeQuality]);

  useEffect(() => {
    currentLessonRef.current = currentLesson;
  }, [currentLesson]);

  useEffect(() => {
    selectedPlaybackRateRef.current = selectedPlaybackRate;
  }, [selectedPlaybackRate]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const selectLesson = (lesson) => {
    // Sadece videoUrl olan dersler seçilebilir
    if (!lesson.videoUrl) {
      return;
    }
    setCurrentLesson(lesson);
    
    const currentSectionId = getCurrentSectionId(lesson.id);
    if (currentSectionId !== null) {
      const newExpandedSections = {};
      newExpandedSections[currentSectionId] = true;
      setExpandedSections(newExpandedSections);
    }
  };

  const getCurrentSectionId = (lessonId) => {
    // Önce "Örnek Videolar" bölümünde kontrol et
    const videoLessons = getVideoLessons();
    if (videoLessons.some(lesson => lesson.id === lessonId)) {
      return 0; // "Örnek Videolar" bölümü ID'si
    }
    
    // Diğer bölümlerde kontrol et
    for (const section of courseData.sections) {
      if (section.lessons.some(lesson => lesson.id === lessonId)) {
        return section.id;
      }
    }
    return null;
  };

  const getNextLesson = () => {
    // Sadece videoUrl olan dersler arasında gezin
    const videoLessons = getVideoLessons();
    const currentIndex = videoLessons.findIndex(l => l.id === currentLesson.id);
    return currentIndex < videoLessons.length - 1 ? videoLessons[currentIndex + 1] : null;
  };

  const getPreviousLesson = () => {
    // Sadece videoUrl olan dersler arasında gezin
    const videoLessons = getVideoLessons();
    const currentIndex = videoLessons.findIndex(l => l.id === currentLesson.id);
    return currentIndex > 0 ? videoLessons[currentIndex - 1] : null;
  };

  const goToNextLesson = () => {
    const next = getNextLesson();
    if (next) {
      selectLesson(next);
    }
  };

  const restartVideo = async () => {
    if (playerRef.current) {
      try {
        playerRef.current.currentTime(0);
        await playerRef.current.play();
      } catch (error) {
        console.error('Video restart hatası:', error);
      }
    }
  };

  const nextLesson = getNextLesson();
  const previousLesson = getPreviousLesson();

  useEffect(() => {
    document.title = 'Örnek Videolar - Yaşasın Hamileyim';
  }, []);

  return (
    <div className="flex flex-col lg:flex-row min-h-screen lg:h-screen bg-gray-900 text-white">
      {/* Sidebar */}
      <div
        className="w-full lg:w-80 2xl:w-1/5 h-auto lg:h-full bg-gray-800 overflow-y-auto flex-shrink-0 lg:order-1 order-2"
        style={computedSidebarStyle}
      >
        <div>
          <div className="p-2 sm:p-4 2xl:p-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10 flex items-center justify-center h-[56px] sm:h-[72px] 2xl:h-[88px]">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-base sm:text-lg 2xl:text-2xl font-bold text-white hover:text-purple-400 transition-colors"
              style={{ textDecoration: 'none' }}
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6 2xl:w-7 2xl:h-7" />
              <span>Ana Sayfaya Dön</span>
            </Link>
          </div>
          <div className="p-3 sm:p-4 2xl:p-6">
            {/* Örnek Videolar Bölümü - En Üstte */}
            <div className="mb-4 2xl:mb-6">
              <button
                onClick={() => toggleSection(0)}
                className="w-full flex items-center justify-between p-3 2xl:p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <span className="font-semibold text-sm 2xl:text-base">Örnek Videolar</span>
                {expandedSections[0] ? <ChevronUp className="w-5 h-5 2xl:w-6 2xl:h-6" /> : <ChevronDown className="w-5 h-5 2xl:w-6 2xl:h-6" />}
              </button>
              
              {expandedSections[0] && (
                <div className="mt-2 2xl:mt-3 space-y-1 2xl:space-y-2">
                  {getVideoLessons().map(lesson => (
                    <button
                      key={lesson.id}
                      onClick={() => selectLesson(lesson)}
                      className={`w-full text-left px-3 py-1.5 2xl:px-4 2xl:py-2 rounded-lg transition-colors flex items-start gap-3 2xl:gap-4 ${
                        currentLesson.id === lesson.id 
                          ? 'bg-purple-600' 
                          : 'hover:bg-gray-700'
                      }`}
                    >
                      <div className="mt-1">
                        <PlayCircle className="w-4 h-4 2xl:w-6 2xl:h-6 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm 2xl:text-base font-medium">{lesson.title}</div>
                        <div className="text-xs 2xl:text-sm text-gray-400">{lesson.duration}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Diğer Bölümler - Sadece Gösterim Amaçlı */}
            {courseData.sections.map(section => (
              <div key={section.id} className="mb-4 2xl:mb-6 opacity-60">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-3 2xl:p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <span className="font-semibold text-sm 2xl:text-base text-gray-300">{section.title}</span>
                  {expandedSections[section.id] ? <ChevronUp className="w-5 h-5 2xl:w-6 2xl:h-6 text-gray-400" /> : <ChevronDown className="w-5 h-5 2xl:w-6 2xl:h-6 text-gray-400" />}
                </button>
                
                {expandedSections[section.id] && (
                  <div className="mt-2 2xl:mt-3 space-y-1 2xl:space-y-2">
                    {section.lessons.map(lesson => {
                      // Bu video "Örnek Videolar" bölümünde mi kontrol et
                      const isInVideoLessons = getVideoLessons().some(vl => vl.id === lesson.id);
                      
                      return (
                      <button
                        key={lesson.id}
                        onClick={() => {
                          // "Örnek Videolar" bölümünde olmayan tüm videolara tıklandığında modal aç
                          if (!isInVideoLessons) {
                            setShowPurchaseModal(true);
                          }
                        }}
                        className={`w-full text-left px-3 py-1.5 2xl:px-4 2xl:py-2 rounded-lg flex items-start gap-3 2xl:gap-4 transition-colors ${
                          isInVideoLessons
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'opacity-75 hover:bg-gray-700/50 cursor-pointer'
                        }`}
                      >
                        <div className="mt-1">
                          <PlayCircle className="w-4 h-4 2xl:w-6 2xl:h-6 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm 2xl:text-base font-medium text-gray-300">{lesson.title}</div>
                          <div className="text-xs 2xl:text-sm text-gray-500">{lesson.duration}</div>
                        </div>
                      </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ana İçerik */}
      <div className="flex-none lg:flex-1 flex flex-col lg:overflow-auto lg:order-2 order-1">
        {/* Üst Bar */}
        <div className="bg-gray-800 p-2 sm:p-4 2xl:p-6 border-b border-gray-700 sticky top-0 z-20 lg:static h-[56px] sm:h-[72px] 2xl:h-[88px] flex items-center">
          <div className="max-w-[2000px] mx-auto flex items-center justify-between gap-2 sm:gap-4 2xl:gap-6 w-full">
            <div className="flex items-center gap-1 sm:gap-2 2xl:gap-3">
              <button
                onClick={() => previousLesson && selectLesson(previousLesson)}
                disabled={!previousLesson}
                className={`px-3 py-2 sm:px-6 sm:py-3 2xl:px-8 2xl:py-4 rounded-lg transition-colors text-sm sm:text-base 2xl:text-lg ${
                  previousLesson 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-700 opacity-50 cursor-not-allowed'
                }`}
              >
                <span className="hidden sm:inline">← Önceki Video</span>
                <span className="sm:hidden">←</span>
              </button>
            </div>

            <h1 className="text-sm sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-center flex-1 truncate px-2 2xl:px-4">{currentLesson.title}</h1>

            <button
              onClick={() => nextLesson && selectLesson(nextLesson)}
              disabled={!nextLesson}
              className={`px-3 py-2 sm:px-6 sm:py-3 2xl:px-8 2xl:py-4 rounded-lg transition-colors text-sm sm:text-base 2xl:text-lg ${
                nextLesson 
                  ? 'bg-purple-600 hover:bg-purple-700' 
                  : 'bg-gray-700 opacity-50 cursor-not-allowed'
              }`}
            >
              <span className="hidden sm:inline">Sonraki Video →</span>
              <span className="sm:hidden">→</span>
            </button>
          </div>
        </div>

        {/* Video Player */}
        <div className="flex-none lg:flex-1 bg-black flex items-start justify-center">
          <div className="w-full flex items-start justify-center">
            <div 
              ref={containerRef}
              style={getVideoContainerStyle()}
            >
              <video
                ref={videoRef}
                className="video-js vjs-theme-sea"
                playsInline
                style={{
                  position: isFullscreen ? 'static' : 'absolute',
                  top: isFullscreen ? 'auto' : 0,
                  left: isFullscreen ? 'auto' : 0,
                  width: '100%',
                  height: '100%'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Satın Al Modal */}
      {showPurchaseModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPurchaseModal(false)}
        >
          <div 
            className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 sm:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Kapatma Butonu */}
            <button
              onClick={() => setShowPurchaseModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* İçerik */}
            <div className="text-center">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-purple-600 rounded-full flex items-center justify-center">
                  <PlayCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  Eğitimin Tamamına Erişim
                </h2>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                  Eğitimin tamamına erişmeniz için satın almanız gerekmektedir.
                </p>
              </div>

              {/* Satın Al Butonu */}
              <Link to="/checkout">
                <button
                  onClick={() => {
                    setShowPurchaseModal(false);
                    // Google Analytics event tracking
                    if (window.gtag) {
                      window.gtag('event', 'purchase_modal_click', {
                        event_category: 'engagement',
                        event_label: 'Satın Al Örnek Videolar',
                        button_location: 'Örnek Videolar Sayfası'
                      });
                    }
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-colors text-base sm:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Satın Al
                </button>
              </Link>

              {/* İptal Butonu */}
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="mt-3 text-gray-400 hover:text-gray-300 text-sm transition-colors"
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrnekVideolar;

